import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  CircularProgress,
  Box,
  Typography,
  Alert,
  Autocomplete,
  TextField
} from '@mui/material';
import { VehicleAssignment, Hub } from '../../../services';

const ASSIGNMENT_IMAGE_MAX_SIZE_MB = 10;
const ASSIGNMENT_IMAGE_MAX_SIZE_BYTES = ASSIGNMENT_IMAGE_MAX_SIZE_MB * 1024 * 1024;
const ASSIGNMENT_IMAGE_SIZE_MESSAGE =
  `File size is too big. Make sure each image is less than ${ASSIGNMENT_IMAGE_MAX_SIZE_MB} MB.`;

interface VehicleAssignmentDialogProps {
  open: boolean;
  availableHubs: Hub[];
  availableVehicles: VehicleAssignment[];
  selectedHub: string;
  selectedVehicle: string;
  vehiclesLoading: boolean;
  onClose: () => void;
  onHubChange: (hubId: string) => void;
  onVehicleChange: (vehicleId: string) => void;
  onVehicleSearchChange: (searchText: string) => void;
  onVehicleSearchOpen: () => void;
  onVehicleSearchClose: () => void;
  onLoadMoreVehicles: () => void;
  hasMoreVehicles: boolean;
  vehicleSearchOpen: boolean;
  dailyRent?: number | null;
  dailyRentLoading?: boolean;
  assigning?: boolean;
  // pass optional images when assigning
  onAssign: (
    assignedAt?: string,
    assigningOdometerCount?: number,
    images?: Record<string, File | null>
  ) => void;
}

const VehicleAssignmentDialog: React.FC<VehicleAssignmentDialogProps> = ({
  open,
  availableHubs,
  availableVehicles,
  selectedHub,
  selectedVehicle,
  vehiclesLoading,
  onClose,
  onHubChange,
  onVehicleChange,
  onVehicleSearchChange,
  onVehicleSearchOpen,
  onVehicleSearchClose,
  onLoadMoreVehicles,
  hasMoreVehicles,
  vehicleSearchOpen,
  dailyRent,
  dailyRentLoading,
  assigning,
  onAssign,
}) => {
  // State for assignment date
  const [assignedAt, setAssignedAt] = React.useState<string>(
    new Date().toISOString().split('T')[0] // Default to today's date in YYYY-MM-DD format
  );
  const [assigningOdometerCount, setAssigningOdometerCount] = React.useState('');

  // Image inputs for vehicle assignment
  const [frontImage, setFrontImage] = React.useState<File | null>(null);
  const [backImage, setBackImage] = React.useState<File | null>(null);
  const [leftImage, setLeftImage] = React.useState<File | null>(null);
  const [rightImage, setRightImage] = React.useState<File | null>(null);
  const [odometerImage, setOdometerImage] = React.useState<File | null>(null);
  const [virtualImage, setVirtualImage] = React.useState<File | null>(null);
  const [chargerImage, setChargerImage] = React.useState<File | null>(null);
  const [batteryImage, setBatteryImage] = React.useState<File | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const requiredImagesSelected = Boolean(
    frontImage && backImage && leftImage && rightImage && odometerImage && virtualImage
  );
  const canAssign = Boolean(
    selectedVehicle &&
    requiredImagesSelected &&
    !dailyRentLoading &&
    dailyRent !== null &&
    dailyRent !== undefined
  );

  const [cameraField, setCameraField] = React.useState<string | null>(null);
  const [cameraError, setCameraError] = React.useState<string | null>(null);
  const [cameraReady, setCameraReady] = React.useState(false);
  const [cameraStream, setCameraStream] = React.useState<MediaStream | null>(null);
  const [cameraSetter, setCameraSetter] = React.useState<React.Dispatch<React.SetStateAction<File | null>> | null>(null);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const stopCameraStream = React.useCallback(() => {
    if (cameraStream) {
      cameraStream.getTracks().forEach((track) => track.stop());
      setCameraStream(null);
    }

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, [cameraStream]);

  const handleCloseCamera = React.useCallback(() => {
    stopCameraStream();
    setCameraField(null);
    setCameraSetter(null);
    setCameraError(null);
    setCameraReady(false);
  }, [stopCameraStream]);

  const handleDialogClose = React.useCallback(() => {
    handleCloseCamera();
    onClose();
  }, [handleCloseCamera, onClose]);

  React.useEffect(() => {
    if (!assigning) {
      setIsSubmitting(false);
    }
  }, [assigning]);

  React.useEffect(() => {
    if (open) {
      setAssigningOdometerCount('');
      setError(null);
    }
  }, [open]);

  const handleOpenCamera = React.useCallback(async (
    field: string,
    setter: React.Dispatch<React.SetStateAction<File | null>>
  ) => {
    if (typeof window === 'undefined' || !navigator.mediaDevices?.getUserMedia) {
      setCameraError('Camera access is not available in this browser.');
      return;
    }

    setCameraError(null);
    setCameraReady(false);
    setCameraField(field);
    setCameraSetter(() => setter);

    try {
      stopCameraStream();
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'environment' },
        audio: false,
      });

      setCameraStream(stream);
    } catch (error) {
      console.error('Camera capture failed', error);
      setCameraError('Unable to open the camera. Please try the upload option instead.');
      handleCloseCamera();
    }
  }, [handleCloseCamera, stopCameraStream]);

  const handleCapturePhoto = React.useCallback(() => {
    if (!videoRef.current || !cameraSetter) {
      setCameraError('Camera preview is not ready yet.');
      return;
    }

    const video = videoRef.current;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;

    const context = canvas.getContext('2d');
    if (!context) {
      setCameraError('Unable to access the camera stream.');
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) {
        setCameraError('Failed to capture the image.');
        return;
      }

      const file = new File([blob], `camera-${Date.now()}.jpg`, {
        type: 'image/jpeg',
      });
      cameraSetter(file);
      handleCloseCamera();
    }, 'image/jpeg', 0.95);
  }, [cameraSetter, handleCloseCamera]);

  React.useEffect(() => {
    if (!cameraStream || !videoRef.current || !cameraField) {
      return;
    }

    const video = videoRef.current;
    video.srcObject = cameraStream;

    const handleReady = () => setCameraReady(true);
    video.addEventListener('canplay', handleReady);

    const playPromise = video.play();
    if (video.readyState >= 3) {
      setCameraReady(true);
    }

    if (playPromise?.catch) {
      playPromise.catch((err) => {
        console.error('Camera play failed', err);
        setCameraError('Unable to open the camera.');
        handleCloseCamera();
      });
    }

    return () => {
      video.removeEventListener('canplay', handleReady);
    };
  }, [cameraStream, cameraField, handleCloseCamera]);

  const handleAssign = () => {
    if (assigning || isSubmitting) return;
    if (!selectedVehicle) return;
    const odometerCount = assigningOdometerCount === '' ? 0 : Number(assigningOdometerCount);
    if (!Number.isInteger(odometerCount) || odometerCount < 0) {
      setError('Odometer count must be a non-negative whole number.');
      return;
    }
    if (odometerCount > 999999) {
      setError('Odometer count should less then or equal to 999999.');
      return;
    }
    if (!requiredImagesSelected) {
      setError('Please upload the six required images before assigning the vehicle. Charger and battery images are optional.');
      return;
    }
    if (dailyRentLoading || dailyRent === null || dailyRent === undefined) {
      setError('Daily rent must be determined before assigning the vehicle.');
      return;
    }

    setError(null);
    setIsSubmitting(true);
    onAssign(assignedAt, odometerCount, {
        frontImage,
        backImage,
        leftImage,
        rightImage,
        odometerImage,
        virtualImage,
        chargerImage,
        batteryImage,
      });
  };

  const handleImageFileChange = (
    file: File | undefined,
    setter: React.Dispatch<React.SetStateAction<File | null>>
  ) => {
    if (!file) {
      setter(null);
      return;
    }

    if (file.size > ASSIGNMENT_IMAGE_MAX_SIZE_BYTES) {
      setter(null);
      setError(ASSIGNMENT_IMAGE_SIZE_MESSAGE);
      return;
    }

    setError(null);
    setter(file);
  };

  // Debug logging
  React.useEffect(() => {
    if (open) {
      console.log('🔵 [VehicleAssignmentDialog] Dialog opened');
      console.log('🔵 [VehicleAssignmentDialog] Available Hubs:', availableHubs.length);
      console.log('🔵 [VehicleAssignmentDialog] Available Vehicles:', availableVehicles.length);
      console.log('🔵 [VehicleAssignmentDialog] Selected Hub:', selectedHub);
      console.log('🔵 [VehicleAssignmentDialog] Selected Vehicle:', selectedVehicle);
      console.log('🔵 [VehicleAssignmentDialog] Vehicles Loading:', vehiclesLoading);
    }
  }, [open, availableHubs, availableVehicles, selectedHub, selectedVehicle, vehiclesLoading]);

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Assign Vehicle to Rider</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <FormControl fullWidth>
            <TextField
              type="date"
              value={assignedAt}
              onChange={(e) => setAssignedAt(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: new Date().toISOString().split('T')[0], // Prevent future dates
              }}
              helperText="Select the date when the vehicle should be assigned"
            />
          </FormControl>

          <TextField
            type="number"
            label="Assigning Odometer Count"
            value={assigningOdometerCount}
            onChange={(event) => setAssigningOdometerCount(event.target.value)}
            inputProps={{ min: 0, max: 999999, step: 1 }}
            error={Number(assigningOdometerCount) > 999999}
            helperText={Number(assigningOdometerCount) > 999999
              ? 'Odometer count should less then or equal to 999999.'
              : 'Enter the odometer reading at assignment'}
            fullWidth
          />

          <FormControl fullWidth>
            <InputLabel>Select Hub</InputLabel>
            <Select
              value={selectedHub}
              label="Select Hub"
              onChange={(e) => onHubChange(e.target.value)}
            >
              {availableHubs.map((hub) => (
                <MenuItem key={hub.id} value={hub.id}>
                  {hub.name} - {hub.cityName || hub.city?.name || 'Unknown City'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth disabled={!selectedHub || vehiclesLoading}>
            <Autocomplete
              fullWidth
              clearOnBlur={false}
              blurOnSelect={false}
              open={vehicleSearchOpen}
              onOpen={onVehicleSearchOpen}
              onClose={onVehicleSearchClose}
              options={availableVehicles}
              value={availableVehicles.find(v => v.id === selectedVehicle) || null}
              onChange={(_, newValue) => {
                if (newValue && typeof newValue !== 'string') {
                  onVehicleChange(newValue.id);
                }
              }}
              onInputChange={(_, newInputValue, reason) => {
                if (reason === 'input') {
                  onVehicleSearchChange(newInputValue);
                }
              }}
              getOptionLabel={(option) =>
                typeof option === 'string' ? option : `${option.registrationNumber} - ${option.make} ${option.model}`
              }
              isOptionEqualToValue={(option, value) => option.id === value?.id}
              loading={vehiclesLoading}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Vehicle"
                  placeholder="Type at least 2 characters to search"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {vehiclesLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  helperText="Search and load available vehicles from the selected hub"
                />
              )}
              ListboxProps={{
                onScroll: (event) => {
                  const listboxNode = event.currentTarget;
                  if (
                    hasMoreVehicles &&
                    !vehiclesLoading &&
                    listboxNode.scrollTop + listboxNode.clientHeight >=
                      listboxNode.scrollHeight - 40
                  ) {
                    onLoadMoreVehicles();
                  }
                },
              }}
              noOptionsText={
                selectedHub
                  ? vehicleSearchOpen
                    ? 'Type 2+ characters to search'
                    : 'Open search to load vehicles'
                  : 'Select a hub first'
              }
            />
          </FormControl>

          {vehiclesLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={100} />
              <Typography variant="body2" color="text.secondary">
                Loading available vehicles...
              </Typography>
            </Box>
          )}

          {selectedVehicle && (
            <Alert severity={dailyRentLoading ? 'info' : dailyRent !== null ? 'success' : 'warning'}>
              {dailyRentLoading
                ? 'Fetching fixed daily rent for the selected hub and vehicle...'
                : dailyRent !== null
                  ? `Fixed daily rent: ₹${dailyRent}`
                  : 'Daily rent could not be determined for the selected hub and vehicle.'}
            </Alert>
          )}

          {!selectedHub && !vehiclesLoading && (
            <Alert severity="info">
              Please select a hub first to view available vehicles for that location.
            </Alert>
          )}

          {selectedHub && !vehiclesLoading && availableVehicles.length === 0 && (
            <Alert severity="warning">
              No available vehicles found in the selected hub. All vehicles may be currently
              assigned or under maintenance.
            </Alert>
          )}

          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {/* Image uploads for assignment evidence */}
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Assignment Images (required)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              The six core assignment images are required. Charger and battery images are optional.
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gap: 1,
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: '1fr 1fr',
                },
              }}
            >
              {[
                { label: 'Front Image', file: frontImage, setter: setFrontImage },
                { label: 'Back Image', file: backImage, setter: setBackImage },
                { label: 'Left Image', file: leftImage, setter: setLeftImage },
                { label: 'Right Image', file: rightImage, setter: setRightImage },
                { label: 'Rider with Vehicle Image', file: odometerImage, setter: setOdometerImage },
                { label: 'Virtual Document Image', file: virtualImage, setter: setVirtualImage },
                { label: 'Charger Image', file: chargerImage, setter: setChargerImage },
                { label: 'Battery Image', file: batteryImage, setter: setBatteryImage },
              ].map(({ label, file, setter }) => {
                const isCameraActive = cameraField === label;
                return (
                  <Box
                    key={label}
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 1,
                      p: 1,
                      border: isCameraActive ? '2px solid' : '1px solid',
                      borderColor: isCameraActive ? 'primary.main' : 'divider',
                      borderRadius: 1,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600}>
                      {label}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        sx={{ textTransform: 'none', flex: 1 }}
                        onClick={() => handleOpenCamera(label, setter)}
                      >
                        {file ? 'Retake photo' : 'Open live camera'}
                      </Button>
                      <Button
                        component="label"
                        variant="outlined"
                        sx={{ textTransform: 'none', flex: 1 }}
                      >
                        {file ? 'Change file' : 'Upload'}
                        <input
                          hidden
                          accept="image/*"
                          type="file"
                          onChange={(e) => {
                            handleImageFileChange(e.target.files?.[0], setter);
                            e.target.value = '';
                          }}
                        />
                      </Button>
                    </Box>
                    {isCameraActive && (
                      <Box sx={{ mt: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, bgcolor: 'background.paper' }}>
                        <Typography variant="subtitle2" color="primary" sx={{ mb: 1, fontWeight: 700 }}>
                          Live camera preview
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Position it clearly and tap capture when ready.
                        </Typography>
                        {cameraError && (
                          <Alert severity="error" sx={{ mb: 1 }}>
                            {cameraError}
                          </Alert>
                        )}
                        <Box
                          sx={{
                            position: 'relative',
                            overflow: 'hidden',
                            borderRadius: 1,
                            bgcolor: 'black',
                            minHeight: 220,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                        >
                          <video
                            ref={videoRef}
                            autoPlay
                            playsInline
                            muted
                            onCanPlay={() => setCameraReady(true)}
                            onLoadedMetadata={() => setCameraReady(true)}
                            onPlaying={() => setCameraReady(true)}
                            style={{
                              width: '100%',
                              maxHeight: 220,
                              objectFit: 'cover',
                              display: cameraReady ? 'block' : 'none',
                            }}
                          />
                          {!cameraReady && (
                            <Typography variant="body2" color="white">
                              Starting camera…
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
                          <Button variant="contained" fullWidth onClick={handleCapturePhoto} disabled={!cameraReady}>
                            Capture photo
                          </Button>
                          <Button variant="outlined" fullWidth onClick={handleCloseCamera}>
                            Cancel
                          </Button>
                        </Box>
                      </Box>
                    )}
                    <Typography variant="body2" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
                      {file ? file.name : 'No image selected'}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button
          onClick={() => {
            handleDialogClose();
            onHubChange('');
            onVehicleChange('');
            setAssignedAt(new Date().toISOString().split('T')[0]); // Reset date
            setAssigningOdometerCount('');
            // Clear selected images
            setFrontImage(null);
            setBackImage(null);
            setLeftImage(null);
            setRightImage(null);
            setOdometerImage(null);
            setVirtualImage(null);
            setChargerImage(null);
            setBatteryImage(null);
            setError(null);
          }}
          disabled={assigning || isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={handleAssign}
          variant="contained"
          disabled={!canAssign || assigning || isSubmitting}
          startIcon={assigning || isSubmitting ? <CircularProgress size={18} color="inherit" /> : undefined}
        >
          {assigning || isSubmitting ? 'Assigning...' : 'Assign'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VehicleAssignmentDialog;
